import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  inject,
  Input,
  OnChanges,
  SecurityContext,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';

import {
  BackgroundColor,
  BlockAlignStyle,
  BorderColor,
  CmsImage,
  CmsUrl,
  ImageLoadingMethod,
  ImageOverlayStyle,
  ImageSizeStyle,
  InlineImage,
  PaddingStyle,
  VisibilitySettingStyle,
} from '@digital-platform/rs-cms/domain';
import { ImageLoading } from '@digital-platform/rs-shared/domain';
import {
  MOBILE,
  MIDSIZE,
  DESKTOP,
  ViewportService,
} from '@digital-platform/rs-shared/services';
import {
  AsNgStylePipe,
  EncodePathPipe,
  PictureComponent,
  SafeHtmlPipe,
} from '@digital-platform/rs-shared/ui';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { RichTextLinkComponent } from '../rich-text-link/rich-text-link.component';
import { NavigationHelperService } from '../utility/navigation-helper.service';

interface StyleDefinition {
  [klass: string]: any;
}

interface MaskConfig {
  direction?: string | null;
  startOpacity?: string | null;
  startPosition?: string | null;
  endOpacity?: string | null;
  endPosition?: string | null;
}

let inlineImageId = 0;

interface InlineImageAngularConfig {
  class?: string;
  mobileWidth?: number;
  mobileHeight?: number;
  midsizeWidth?: number;
  midsizeHeight?: number;
  desktopWidth?: number;
  desktopHeight?: number;
  mobileClass?: string;
  midsizeClass?: string;
  desktopClass?: string;
  maxWidth?: boolean;
  heightAuto?: boolean;
}

@Component({
  selector: 'digital-platform-inline-image',
  standalone: true,
  imports: [
    CommonModule,
    PictureComponent,
    RichTextLinkComponent,
    RouterModule,
    SafeHtmlPipe,
    AsNgStylePipe,
    EncodePathPipe,
  ],
  providers: [AsNgStylePipe, EncodePathPipe],
  templateUrl: './inline-image.component.html',
  styleUrls: ['./inline-image.component.scss'],
})
export class InlineImageComponent
  extends BasePTypeComponent
  implements InlineImage, OnChanges
{
  @HostBinding('class') hostClassName: string | null = null;
  public override type = 'inlineImage' as const;

  public sanitizer = inject(DomSanitizer);
  private asNgStyle = inject(AsNgStylePipe);
  private viewportService = inject(ViewportService);
  private navigationHelperService = inject(NavigationHelperService);

  public emptyCmsImage = {
    target_id: null,
    image_use: null,
    alt: null,
    title: null,
    width: null,
    height: null,
    src: '',
  };

  public defaultImageLoading = {
    tid: 5943,
    name: 'Lazy',
    value: ImageLoading.enum.lazy,
  };

  @Input() public inlineImageOverride: InlineImage | undefined;

  @Input() public topLeft = false;
  @Input() public hideOnMobile = true;
  @Input() public analyticsLinkText: string | null = '';
  @Input() public blockAlign: BlockAlignStyle | null = {};
  @Input() public borderColor: BorderColor | null = {};
  @Input() public borderWidth: string | null = '';
  @Input() public class: string | null = '';
  @Input() public endOpacityPercent: string | null = '';
  @Input() public endOpacityPercentMobile: string | null = '';
  @Input() public endPositionPercent: string | null = '';
  @Input() public endPositionPercentMobil: string | null = '';
  @Input() public gradientBackgroundColor: BackgroundColor | null = {};
  @Input() public gradientDirection: string | null = '';
  @Input() public gradientDirectionMobile: string | null = '';
  @Input() public imageHeight: number | null = null;
  @Input() public loading: ImageLoadingMethod | null = this.defaultImageLoading;
  @Input() public imageOverlayColor: BackgroundColor | null = {};
  @Input() public imageOverlayStyle: ImageOverlayStyle | null = {};
  @Input() public imageSize: ImageSizeStyle | null = {};
  @Input() public imageSizeMidrange: ImageSizeStyle | null = {};
  @Input() public imageSizeMobile: ImageSizeStyle | null = {};
  @Input() public imageWidth: number | null = null;
  @Input() public padding: PaddingStyle | null = {};
  @Input() public paddingHorizontal: PaddingStyle | null = {};
  @Input() public paddingVertical: PaddingStyle | null = {};
  @Input() public paddingTop: PaddingStyle | null = {};
  @Input() public paddingRight: PaddingStyle | null = {};
  @Input() public paddingBottom: PaddingStyle | null = {};
  @Input() public paddingLeft: PaddingStyle | null = {};
  @Input() public roundedCornerRadius: string | null = '';
  @Input() public startOpacityPercent: string | null = '';
  @Input() public startOpacityPercentMobi: string | null = '';
  @Input() public startPositionPercent: string | null = '';
  @Input() public startPositionPercentMob: string | null = '';
  @Input() public url: CmsUrl | null = [];
  @Input() public visibilitySetting: VisibilitySettingStyle | null = {};
  @Input() public webpMedia: CmsImage | null = null;
  @Input() public webpMediaMid: CmsImage | null = null;
  @Input() public webpMediaDesktop: CmsImage | null = null;
  @Input() public forceImageToContainer = false;
  @Input() public autoWidth = false;
  @Input() public autoHeight = false;
  @Input() public width100percent = false;
  @Input() public height100percent = false;
  @Input() public angularConfig?: InlineImageAngularConfig;
  @Input() public excludeImageOverride: string[] = [];
  public imageCls = '';
  public imageWrapperCls = '';
  public imageWrapperMiddleCls = '';
  public imageWrapperInnerCls = '';
  public imageWrapperInnerStyles = {};
  public imageLoading: ImageLoading = ImageLoading.enum.eager;
  public inlineImageId = 'inline-img-' + inlineImageId++;
  public mq = { mobile: MOBILE, midsize: MIDSIZE, desktop: DESKTOP };

  get mediaQueryStyles() {
    const selector = `img#${this.inlineImageId}`;
    const mobile = this.asNgStyle.transform(this.imgStyleMobile);
    const midsize = this.asNgStyle.transform(this.imgStyleMid);
    const desktop = this.asNgStyle.transform(this.imgStyleDesktop);
    return `
      <style>
      @media ${MOBILE} {${selector}{${mobile}}}
      @media ${MIDSIZE} {${selector}{${midsize}}}
      @media ${DESKTOP} {${selector}{${desktop}}}
      </style>
    `;
  }

  get mobileWebpMedia(): CmsImage {
    return (
      this.webpMedia ||
      this.webpMediaMid ||
      this.webpMediaDesktop ||
      this.emptyCmsImage
    );
  }

  get midWebpMedia(): CmsImage {
    return (
      this.webpMediaMid ||
      this.webpMediaDesktop ||
      this.webpMedia ||
      this.emptyCmsImage
    );
  }

  get desktopWebpMedia(): CmsImage {
    return (
      this.webpMediaDesktop ||
      this.webpMediaMid ||
      this.webpMedia ||
      this.emptyCmsImage
    );
  }

  get fallbackWebpMedia(): CmsImage {
    const webpMedia = this.desktopWebpMedia;
    const src = String(webpMedia.src)
      .replace(/\/styles\/[^/]+\/public\//, '/')
      .replace(/\.webp$/, '');
    return { ...webpMedia, src };
  }

  public imgStyleDesktop: StyleDefinition = {};
  public imgStyleMobile: StyleDefinition = {};
  public imgStyleMid: StyleDefinition = {};
  public roundedCornerStyle: StyleDefinition = {};

  public paddingClasses = '';

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.inlineImageOverride) {
      // Override all values coming from a single object.
      Object.assign(this, this.inlineImageOverride);
      this.excludeImageOverride =
        changes['excludeImageOverride']?.currentValue || [];
      Object.keys(changes).forEach((key) => {
        if (this.excludeImageOverride.includes(key)) {
          (this as any)[key] = changes[key].currentValue;
        }
      });
      if (this.angularConfig && typeof this.angularConfig === 'string') {
        try {
          this.angularConfig = JSON.parse(this.angularConfig);
        } catch (e) {
          console.error(`Unable to parse ${this.angularConfig} as JSON.`);
        }
      }
    }

    this.initImageClasses();
    this.initWrapperInnerStyles();
    this.initPaddingClasses();
    this.initBorderStyles();
    this.initWidthHeightStyle();
    this.initMaskImageStyle();

    if (this.loading) {
      this.imageLoading =
        this.loading.value === ImageLoading.enum.lazy
          ? ImageLoading.enum.lazy
          : ImageLoading.enum.eager;
    } else {
      this.imageLoading = this.defaultImageLoading.value as ImageLoading;
    }
  }

  getMediaType(src?: string | null): string | null {
    if (/\.webp$/i.test(src as string)) {
      return 'image/webp';
    } else if (/\.png$/i.test(src as string)) {
      return 'image/png';
    } else if (/\.jpe?g$/i.test(src as string)) {
      return 'image/jpeg';
    } else if (src) {
      return 'image/' + src.split('.').pop();
    }
    return null;
  }

  stringOrNullToString(cssClass: string | null = ''): string {
    const retVal: string = cssClass || '';
    return this.sanitizer.sanitize(SecurityContext.NONE, retVal) || '';
  }

  private initMaskImageStyle() {
    this.imgStyleDesktop['-webkit-mask-image'] =
      this.imgStyleDesktop['mask-image'] =
      this.imgStyleMid['-webkit-mask-image'] =
      this.imgStyleMid['mask-image'] =
        this.getProperMaskFromAttributes({
          direction: this.gradientDirection,
          startPosition: this.startPositionPercent,
          startOpacity: this.startOpacityPercent,
          endPosition: this.endPositionPercent,
          endOpacity: this.endOpacityPercent,
        });

    this.imgStyleMobile['-webkit-mask-image'] = this.imgStyleMobile[
      'mask-image'
    ] = this.getProperMaskFromAttributes({
      direction: this.gradientDirectionMobile,
      startPosition: this.startPositionPercentMob,
      startOpacity: this.startOpacityPercentMobi,
      endPosition: this.endPositionPercentMobil,
      endOpacity: this.endOpacityPercentMobile,
    });
  }

  private initWidthHeightStyle() {
    if (this.forceImageToContainer) {
      this.imgStyleMobile['width'] = this.toCssValue(
        this.angularConfig?.mobileWidth,
        '100%'
      );
      this.imgStyleMobile['height'] = this.toCssValue(
        this.angularConfig?.mobileHeight,
        '100%'
      );
      this.imgStyleMid['width'] = this.toCssValue(
        this.angularConfig?.midsizeWidth,
        '100%'
      );
      this.imgStyleMid['height'] = this.toCssValue(
        this.angularConfig?.midsizeHeight,
        '100%'
      );
      this.imgStyleDesktop['width'] = this.toCssValue(
        this.angularConfig?.desktopWidth,
        '100%'
      );
      this.imgStyleDesktop['height'] = this.toCssValue(
        this.angularConfig?.desktopHeight,
        '100%'
      );
    } else {
      // desktop
      this.imgStyleDesktop['width'] = this.getWidth(
        this.angularConfig?.desktopWidth,
        this.imageWidth,
        this.desktopWebpMedia.width
      );
      this.imgStyleDesktop['height'] = this.getHeight(
        this.angularConfig?.desktopHeight,
        this.imageHeight,
        this.desktopWebpMedia.height
      );
      // midsize
      this.imgStyleMid['width'] = this.getWidth(
        this.angularConfig?.midsizeWidth,
        this.imageWidth,
        this.midWebpMedia.width
      );
      this.imgStyleMid['height'] = this.getHeight(
        this.angularConfig?.midsizeHeight,
        this.imageHeight,
        this.midWebpMedia.height
      );
      // mobile
      this.imgStyleMobile['width'] = this.getWidth(
        this.angularConfig?.mobileWidth,
        this.imageWidth,
        this.mobileWebpMedia.width
      );
      this.imgStyleMobile['height'] = this.getHeight(
        this.angularConfig?.mobileHeight,
        this.imageHeight,
        this.mobileWebpMedia.height
      );
    }
  }

  private getWidth(
    configWidth: number | null | undefined,
    ...widths: Array<number | null | undefined>
  ) {
    return this.autoWidth
      ? this.toCssValue(configWidth, 'auto')
      : this.width100percent
      ? this.toCssValue(configWidth, '100%')
      : this.toCssValue(configWidth, ...widths, 'auto');
  }

  private getHeight(
    configHeight: number | null | undefined,
    ...heights: Array<number | null | undefined>
  ) {
    return this.autoHeight
      ? this.toCssValue(configHeight, 'auto')
      : this.height100percent
      ? this.toCssValue(configHeight, '100%')
      : this.toCssValue(configHeight, ...heights, 'auto');
  }

  private toCssValue(
    ...values: Array<number | null | undefined | string>
  ): string | undefined {
    for (let i = 0; i < values.length; i++)
      if (typeof values[i] === 'number') return `${values[i]}px`;
    const defaultValue = values[values.length - 1];
    if (typeof defaultValue === 'number') return `${defaultValue}px`;
    return defaultValue ? String(defaultValue) : 'auto';
  }

  private initBorderStyles() {
    const borderWidth = this.borderWidth ? this.borderWidth : '0';

    // Plug in border width/style.
    this.roundedCornerStyle['border-width'] = `${borderWidth}px`;
    this.roundedCornerStyle['border-style'] = 'solid';

    // Verify all rounded corner inputs needed for the feature are present and define rounded corner style.
    if (this.roundedCornerRadius && this.imageOverlayStyle) {
      let prop = '';
      switch (this.imageOverlayStyle?.overlayStyle) {
        case 'rounded-corner-ne':
          prop = 'border-top-right-radius';
          break;
        case 'rounded-corner-se':
          prop = 'border-bottom-right-radius';
          break;
        case 'rounded-corner-sw':
          prop = 'border-bottom-left-radius';
          break;
        case 'rounded-corner-nw':
          prop = 'border-top-left-radius';
          break;
      }
      if (prop) {
        const cornerRadius = this.roundedCornerRadius
          ? +this.roundedCornerRadius
          : 0;
        this.roundedCornerStyle[prop] = `${cornerRadius}px`;
        const imgCornerRadius = cornerRadius - +borderWidth;
        this.imgStyleMobile[prop] =
          this.imgStyleMid[prop] =
          this.imgStyleDesktop[prop] =
            `${imgCornerRadius}px`;
      }
    }
  }

  // Verify all image gradient inputs needed for the feature are present and define desktop and mobile styles before plugging those in.
  private getProperMaskFromAttributes(
    maskConfig: MaskConfig
  ): string | undefined {
    const { direction, startOpacity, startPosition, endOpacity, endPosition } =
      maskConfig;
    if (
      direction &&
      startOpacity &&
      startPosition &&
      endPosition &&
      endOpacity &&
      (startOpacity !== '100.00' || endOpacity !== '100.00')
    ) {
      return `linear-gradient(${direction}deg, rgba(0,0,0,${startOpacity}%) ${startPosition}%, rgba(0,0,0,${endOpacity}%) ${endPosition}%)`;
    }
    return undefined;
  }

  private initPaddingClasses() {
    // Add padding inputs to paddingClassesAry and collapse into a string.
    const classes: Array<string | null | undefined | false> = [
      this.padding?.paddingClass,
      this.paddingHorizontal?.paddingClass,
      this.paddingVertical?.paddingClass,
      this.paddingTop?.paddingClass,
      this.paddingRight?.paddingClass,
      this.paddingBottom?.paddingClass,
      this.paddingLeft?.paddingClass,
    ];
    this.paddingClasses = classes.filter((c) => c).join(' ');
  }

  private initImageClasses() {
    this.imageWrapperCls = [
      'inline-image-component-wrapper',
      this.stringOrNullToString(this.paddingClasses),
      this.stringOrNullToString(this.blockAlign?.alignmentClass),
    ].join(' ');
    this.imageWrapperMiddleCls = [
      'iic-wrapper-middle',
      this.stringOrNullToString(this.imageOverlayColor?.cssColorName),
    ].join(' ');

    this.imageWrapperInnerCls = [
      'iic-wrapper-inner',
      this.stringOrNullToString(this.gradientBackgroundColor?.cssColorName),
      this.stringOrNullToString(this.borderColor?.cssColorName),
    ].join(' ');

    this.hostClassName = this.visibilitySetting?.visibilityClass ?? '';

    const cls: Array<string | undefined | false | null> = [
      // Set the image to max-width: 100%
      'image mw-100',
      this.class,
    ];

    if (this.angularConfig) {
      const {
        class: angClass,
        maxWidth,
        heightAuto,
        mobileClass,
        midsizeClass,
        desktopClass,
      } = this.angularConfig;
      const { isMobile, isMidsize, isDesktop } = this.viewportService;

      cls.push(
        maxWidth && !this.class?.includes(`mw-100`) && 'mw-100',
        heightAuto && !this.class?.includes(`h-auto`) && 'h-auto',
        angClass,
        isMobile && mobileClass,
        isMidsize && midsizeClass,
        isDesktop && desktopClass
      );
    }

    this.imageCls = cls.filter((c) => c).join(' ');
  }

  private initWrapperInnerStyles() {
    if (this.forceImageToContainer) {
      this.imageWrapperInnerStyles = { width: '100%', height: '100%' };
    } else {
      this.imageWrapperInnerStyles = {
        width: this.imageWidth ? `${this.imageWidth}px` : 'inherit',
        height: this.imageHeight ? `${this.imageHeight}px` : 'inherit',
      };
    }
  }

  get innerWrapperCls() {
    return { ...this.roundedCornerStyle, ...this.imageWrapperInnerStyles };
  }

  public navigate(event: Event, url: string): void {
    this.navigationHelperService.navigate(
      event,
      url,
      this.navigationHelperService.isExternalUrl(url)
    );
  }
}
